import './ProductDeepDive.scss';
import cn from 'classnames';
import { KeyboardEvent, useMemo, useState } from 'react';
import { MdExpandMore } from 'react-icons/md';
import { Dropdown } from '@Components/Dropdown';
import { Placement, StretchMode } from '@Components/designConstants';
import { AsinMap, AsinRow } from '../category-pathing-types';
import { ValueMode, ValueModeType } from '../category-pathing-constants';
import { formatDays, formatInt, formatPercent } from '../utils/format';

const VALUE_MODE_OPTIONS = [
  { label: '% View', value: ValueMode.PERCENT },
  { label: 'Actual Values', value: ValueMode.COUNT }
];

interface ProductDeepDiveProps {
  asinRows: AsinRow[];
  asinMap: AsinMap;
  deepDiveCategories: string[];
}

interface EntryProduct {
  asin: string;
  productName: string;
  totalUsers: number;
  repurchaseUsers: number;
  crossSellUsers: number;
  avgDaysToFollowup: number | null;
}

interface FollowupItem {
  followupAsin: string;
  productName: string;
  users: number;
  avgDays: number | null;
  isRepurchase: boolean;
}

interface ProductRowProps {
  product: EntryProduct;
  followups: FollowupItem[];
  expanded: boolean;
  onToggle: () => void;
  valueMode: ValueModeType;
  brandHaloColumnTotal: number;
}

const pctOf = (numerator: number, denominator: number): string =>
  denominator > 0 ? formatPercent((numerator / denominator) * 100) : '—';

const ProductNameCell = ({ name, asin }: { name: string; asin: string }) => (
  <div className='product-name-cell'>
    <div className='product-name'>{name}</div>
    <div className='product-asin'>{asin}</div>
  </div>
);

const ProductRow = ({
  product,
  followups,
  expanded,
  onToggle,
  valueMode,
  brandHaloColumnTotal
}: ProductRowProps) => {
  const handleKeyDown = (e: KeyboardEvent<HTMLTableRowElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  };
  const isPct = valueMode === ValueMode.PERCENT;
  const brandHaloDisplay = isPct
    ? pctOf(product.totalUsers, brandHaloColumnTotal)
    : formatInt(product.totalUsers);
  const sameAsinDisplay = isPct
    ? pctOf(product.repurchaseUsers, product.totalUsers)
    : formatInt(product.repurchaseUsers);
  const otherAsinDisplay = isPct
    ? pctOf(product.crossSellUsers, product.totalUsers)
    : formatInt(product.crossSellUsers);
  const subTableTotal = followups.reduce((s, f) => s + f.users, 0);
  return (
    <>
      <tr
        className={cn('entry-row', { expanded })}
        onClick={onToggle}
        onKeyDown={handleKeyDown}
        role='button'
        tabIndex={0}
        aria-expanded={expanded}
      >
        <td className='expand-cell'>
          <span className={cn('expand-toggle', { expanded })} aria-hidden='true'>
            <MdExpandMore />
          </span>
        </td>
        <td className='product-cell'>
          <ProductNameCell name={product.productName} asin={product.asin} />
        </td>
        <td className='num-cell brand-halo-col'>{brandHaloDisplay}</td>
        <td className='num-cell'>{sameAsinDisplay}</td>
        <td className='num-cell'>{otherAsinDisplay}</td>
        <td className='num-cell'>
          {product.avgDaysToFollowup === null ? '—' : formatDays(product.avgDaysToFollowup)}
        </td>
      </tr>
      {expanded && (
        <tr className='followups-row'>
          <td colSpan={6}>
            <div className='followups-table-wrap'>
              <div className='followups-header'>
                Brand halo repurchases after purchasing {product.productName}
              </div>
              <table className='followups-table'>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Repurchase Product</th>
                    <th className='num-col'>Total</th>
                    <th className='num-col'>Days to Repurchase</th>
                  </tr>
                </thead>
                <tbody>
                  {followups.map(f => (
                    <tr key={f.followupAsin}>
                      <td className='type-cell'>
                        <span className={cn('badge', f.isRepurchase ? 'repurchase' : 'cross-sell')}>
                          {f.isRepurchase ? 'Repeat Purchase' : 'Other ASIN'}
                        </span>
                      </td>
                      <td>
                        <ProductNameCell name={f.productName} asin={f.followupAsin} />
                      </td>
                      <td className='num-cell'>
                        {isPct ? pctOf(f.users, subTableTotal) : formatInt(f.users)}
                      </td>
                      <td className='num-cell'>{formatDays(f.avgDays)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export const ProductDeepDive = ({
  asinRows,
  asinMap,
  deepDiveCategories
}: ProductDeepDiveProps) => {
  const [selectedCategory, setSelectedCategory] = useState<string>(deepDiveCategories[0] ?? '');
  const [valueMode, setValueMode] = useState<ValueModeType>(ValueMode.PERCENT);
  const [expandedAsins, setExpandedAsins] = useState<Set<string>>(new Set());

  const productsInCategory = useMemo<EntryProduct[]>(() => {
    const byEntry = new Map<string, AsinRow[]>();
    for (const r of asinRows) {
      const cat = asinMap[r.ntbAsin]?.category;
      if (cat !== selectedCategory) continue;
      if (!byEntry.has(r.ntbAsin)) {
        byEntry.set(r.ntbAsin, []);
      }
      byEntry.get(r.ntbAsin)!.push(r);
    }
    const out: EntryProduct[] = [];
    for (const [asin, rows] of byEntry.entries()) {
      const totalUsers = rows.reduce((s, r) => s + r.ntbUsersWithFollowup, 0);
      const repurchaseUsers = rows
        .filter(r => r.isRepurchase)
        .reduce((s, r) => s + r.ntbUsersWithFollowup, 0);
      // Re-pool weighting by each row's timed-user count (avgDaysUsers), not its total users,
      // so the mean stays correct as timing coverage varies. null when no row reported timing.
      const timedUsers = rows.reduce((s, r) => s + r.avgDaysUsers, 0);
      const avgDaysToFollowup =
        timedUsers > 0
          ? rows.reduce((s, r) => s + (r.avgDaysToFollowup ?? 0) * r.avgDaysUsers, 0) / timedUsers
          : null;
      out.push({
        asin,
        productName: asinMap[asin]?.productName ?? asin,
        totalUsers,
        repurchaseUsers,
        crossSellUsers: totalUsers - repurchaseUsers,
        avgDaysToFollowup
      });
    }
    return out.sort((a, b) => b.totalUsers - a.totalUsers);
  }, [asinRows, asinMap, selectedCategory]);

  const followupsByEntry = useMemo(() => {
    const m = new Map<string, FollowupItem[]>();
    for (const r of asinRows) {
      const cat = asinMap[r.ntbAsin]?.category;
      if (cat !== selectedCategory) continue;
      if (!m.has(r.ntbAsin)) {
        m.set(r.ntbAsin, []);
      }
      m.get(r.ntbAsin)!.push({
        followupAsin: r.followupAsin,
        productName: asinMap[r.followupAsin]?.productName ?? r.followupAsin,
        users: r.ntbUsersWithFollowup,
        avgDays: r.avgDaysToFollowup,
        isRepurchase: r.isRepurchase
      });
    }
    for (const items of m.values()) {
      items.sort((a, b) => b.users - a.users);
    }
    return m;
  }, [asinRows, asinMap, selectedCategory]);

  const dropdownOptions = useMemo(
    () => deepDiveCategories.map(c => ({ label: c, value: c })),
    [deepDiveCategories]
  );

  const brandHaloColumnTotal = useMemo(
    () => productsInCategory.reduce((s, p) => s + p.totalUsers, 0),
    [productsInCategory]
  );

  const toggleExpanded = (asin: string) => {
    setExpandedAsins(prev => {
      const next = new Set(prev);
      if (next.has(asin)) {
        next.delete(asin);
      } else {
        next.add(asin);
      }
      return next;
    });
  };

  const handleCategoryChange = (cat: string) => {
    setSelectedCategory(cat);
    setExpandedAsins(new Set());
  };

  return (
    <div className='product-deep-dive'>
      <div className='deep-dive-controls'>
        <Dropdown
          overlayPlacement={Placement.RIGHT}
          stretch={StretchMode.FIT}
          options={dropdownOptions}
          value={selectedCategory}
          onChange={handleCategoryChange}
          label='Entry category'
        />
        <Dropdown
          overlayPlacement={Placement.RIGHT}
          stretch={StretchMode.FIT}
          options={VALUE_MODE_OPTIONS}
          value={valueMode}
          onChange={setValueMode}
        />
        <div className='deep-dive-summary'>
          {productsInCategory.length} products · click a row to see follow-up purchases
        </div>
      </div>
      <div className='deep-dive-table-wrap'>
        <table className='deep-dive-table'>
          <thead>
            <tr>
              <th className='expand-col' aria-label='Expand row' />
              <th className='entry-col'>Entry product</th>
              <th className='num-col brand-halo-col'>Brand Halo Repurchase</th>
              <th className='num-col'>Same ASIN Repeat Purchase</th>
              <th className='num-col'>Other ASIN Repurchase</th>
              <th className='num-col'>Days to Repurchase</th>
            </tr>
          </thead>
          <tbody>
            {productsInCategory.map(p => (
              <ProductRow
                key={p.asin}
                product={p}
                followups={followupsByEntry.get(p.asin) ?? []}
                expanded={expandedAsins.has(p.asin)}
                onToggle={() => toggleExpanded(p.asin)}
                valueMode={valueMode}
                brandHaloColumnTotal={brandHaloColumnTotal}
              />
            ))}
          </tbody>
        </table>
        {productsInCategory.length === 0 && (
          <div className='deep-dive-empty'>No ASIN-level pathing rows for this category.</div>
        )}
      </div>
    </div>
  );
};
